import React, { useState, useEffect } from 'react';
import { CheckCircle, Send, Loader2, Zap } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

/**
 * In-course micro quiz (Practice step). Loads linked assessment by courseId.
 */
const CoursePracticeQuiz = ({ courseId, instructions, onQuizComplete, disabled }) => {
  const [assessment, setAssessment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/assessments/micro/by-course/${courseId}`);
        if (data.success && data.data) {
          setAssessment(data.data);
          const qs = data.data.randomizeQuestions !== false
            ? shuffle(data.data.questions || [])
            : (data.data.questions || []);
          setQuestions(qs);
        }
      } catch {
        setAssessment(null);
      } finally {
        setLoading(false);
      }
    };
    if (courseId) load();
  }, [courseId]);

  const handleSubmit = async () => {
    if (!assessment?._id) return;
    if (Object.keys(answers).length < questions.length) {
      toast.error('Please answer all questions');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/assessments/${assessment._id}/submit`, {
        answers: questions.map((q) => ({
          questionId: q._id,
          selectedValue: answers[q._id],
        })),
        timeTaken: 0,
      });
      if (data.success) {
        setResult(data.data);
        toast.success(`Score: ${data.data.percentage}%`);
        if (onQuizComplete) onQuizComplete(data.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-500 text-sm">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading quiz…
      </div>
    );
  }

  if (!assessment || questions.length === 0) {
    return (
      <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-xl p-4">
        Quiz is not configured yet. Save the course with at least one question in the Practice step (Quiz type).
      </p>
    );
  }

  if (result) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center space-y-2">
        <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto" />
        <p className="font-black text-emerald-800 text-lg">{result.percentage}%</p>
        <p className="text-sm text-emerald-700">
          {result.score} of {result.total} correct — attempt #{result.attemptNumber}
        </p>
        <p className="text-xs text-gray-500">You can mark this practice step complete below.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {assessment.randomizeQuestions && (
        <p className="text-xs text-violet-600 flex items-center gap-1 font-semibold">
          <Zap className="w-3.5 h-3.5" /> Questions are shuffled for your attempt
        </p>
      )}
      {instructions && (
        <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-xl border border-slate-100">{instructions}</p>
      )}
      {questions.map((q, idx) => (
        <div key={q._id} className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
          <p className="text-sm font-bold text-slate-800">
            <span className="text-slate-400 mr-1">Q{idx + 1}.</span>
            {q.questionText}
          </p>
          <div className="space-y-2">
            {(q.options || []).map((opt) => (
              <button
                key={opt.value}
                type="button"
                disabled={disabled}
                onClick={() => setAnswers((prev) => ({ ...prev, [q._id]: opt.value }))}
                className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm font-medium transition ${
                  answers[q._id] === opt.value
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-800'
                    : 'border-gray-200 hover:border-indigo-300'
                }`}
              >
                <span className="font-black mr-2">{opt.value}.</span>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={disabled || submitting || Object.keys(answers).length < questions.length}
        className="w-full py-3 bg-indigo-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        Submit Quiz
      </button>
    </div>
  );
};

export default CoursePracticeQuiz;
